/**
 * VireoHR - Protected Route Component
 * Handles role-based route protection
 */

import { Navigate, Outlet } from 'react-router-dom';

import { useAuth, UserRole } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  allowedRoles: UserRole[];
}

export default function ProtectedRoute({ allowedRoles }: ProtectedRouteProps) {
  const { user, isAuthenticated, isLoading } = useAuth();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-[#0D9488] flex items-center justify-center animate-pulse">
            <span className="text-3xl font-bold text-[#D4AF37]">V</span>
          </div>
          <p className="text-[#0D9488] font-semibold">Loading...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user role is allowed
  if (!allowedRoles.includes(user.role)) {
    // Redirect to appropriate dashboard based on role
    switch (user.role) {
      case 'owner':
        return <Navigate to="/owner/home" replace />;
      case 'employee':
        return <Navigate to="/employee/home" replace />;
      case 'manager':
        return <Navigate to="/manager/home" replace />;
      case 'admin':
        return <Navigate to="/admin/tenants" replace />;
      case 'super_admin':
        return <Navigate to="/admin/tenants" replace />;
      default:
        return <Navigate to="/login" replace />;
    }
  }

  // User is authenticated and has the right role
  return <Outlet />;
}
